'use client';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import type { FetchProductsTagsQuery } from '@eliadmin/core/src/api/products/product-api';
import { fetchProducts, fetchProductsTags } from '@eliadmin/core/src/api/products/product-api';
import type { ProductCarousel } from '@eliadmin/core/src/components/product/product-carousel';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  type BuildProductQueryOptions,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Suspense, type ComponentPropsWithoutRef, useRef } from 'react';
import type { EmblaCarouselType } from 'embla-carousel';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
export type ProductLabelsProps = {
  query?: BuildProductQueryOptions;
  tagsQuery?: FetchProductsTagsQuery;
  title?: string;
  id?: string;
  className?: string;
  before?: React.ReactNode;
  after?: React.ReactNode;
  style?: React.CSSProperties;
  carouselProps?: Omit<ComponentPropsWithoutRef<typeof ProductCarousel>, 'products'>;
  children?: React.ReactNode;
  showTags?: boolean;
  buttonLabel?: string;
  showMoreLink?: boolean;
};

function ProductLabelsInternal({
  query: queryOpts,
  showMoreLink = true,
  tagsQuery = { show_homepage: true },
  ...props
}: ProductLabelsProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();
  const emblaRef = useRef<EmblaCarouselType>(null);

  const { data: tags } = useSuspenseQuery({
    queryKey: [CacheKey.PRODUCT_TAGS, tagsQuery],
    queryFn: () =>
      fetchProductsTags({ query: tagsQuery, locale }).catch(() => {
        return { data: [] };
      }),
  });

  const query = buildProductsQuery({ ...queryOpts, tags: tags?.data?.map(tag => tag.id) ?? [] });

  const productRequestUri = buildProductsRequest(query).uri;

  const { data: products } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, productRequestUri, me?.customer.id ?? null],
    queryFn: () =>
      fetchProducts({ query, locale, authToken: me?.authToken }).catch(() => {
        return { data: [] };
      }),
    enabled: isInitialized,
  });

  if (!products?.data?.length || !tags?.data?.length) return null;

  const displayTag = tags.data[0];

  return (
    <section
      id={props.id}
      className={cn('ea-product-labels-container', props.className)}
      style={props.style}
    >
      <Container
        size="xl"
        className={cn('flex flex-col gap-6', 'ea-product-labels-container__container')}
      >
        <div
          className={cn(
            'flex w-full flex-row items-center justify-between',
            'ea-product-labels-container__header',
          )}
        >
          <h2
            className={cn(
              'flex items-start gap-x-5 text-left text-2xl font-bold md:flex-row md:items-center md:justify-center',
              'ea-product-labels-container__title',
            )}
          >
            <span className="w-full min-w-fit">{props.title}</span>
            {/* nav btns */}
            <div className="flex w-full items-center justify-start gap-5">
              <div className={cn('hidden gap-2 md:flex', 'ea-blog-news__nav-buttons')}>
                <Button
                  variant="outline"
                  className={cn(
                    'h-10 w-10 rounded-full border border-border p-2 text-primary shadow-none',
                    'ea-blog-news__nav-button ea-blog-news__nav-button-left',
                  )}
                >
                  {/* TODO Icons to some config */}
                  <LuChevronLeft
                    className="h-4 w-4"
                    onClick={() => {
                      emblaRef.current?.scrollPrev();
                    }}
                  />
                </Button>
                <Button
                  variant="outline"
                  className={cn(
                    'h-10 w-10 rounded-full border border-border p-2 text-primary shadow-none',
                    'ea-blog-news__nav-button ea-blog-news__nav-button-right',
                  )}
                  onClick={() => {
                    emblaRef.current?.scrollNext();
                  }}
                >
                  <LuChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </h2>
          {props.showTags && (
            <div
              className={cn(
                'flex flex-row flex-wrap justify-end gap-2',
                'ea-product-labels-container__tags',
              )}
            >
              {tags.data?.map(tag => {
                return (
                  <Badge
                    size={'md'}
                    variant="outline"
                    style={{
                      borderColor: tag.color,
                      color: tag.color,
                    }}
                    key={tag.id}
                    className={cn('flex flex-row gap-2', 'ea-product-labels-container__tag')}
                  >
                    {tag.name}
                  </Badge>
                );
              })}
            </div>
          )}
          <div className=" hidden items-center justify-end gap-2 md:flex">
            {showMoreLink && (
              <Button
                variant="accent"
                asChild
                className={cn(
                  'min-w-fit bg-input text-[#1E3C78] hover:bg-input',
                  'ea-product-labels-container__button',
                )}
              >
                <Link href={`/${displayTag.slug}`} prefetch={false}>
                  {props.title ?? t('text338')}
                </Link>
              </Button>
            )}
          </div>
        </div>
        <div className={cn('flex flex-row gap-4', 'ea-product-labels-container__content')}>
          {/* {props.before}
          <ProductCarousel {...props.carouselProps} products={products.data} />
          {props.after} */}
          <Carousel
            emblaRef={emblaRef}
            className={{
              main: 'w-full @container',
              container: 'min-w-full',
              slide: 'w-full p-2 @sm:basis-1/2 @2xl:basis-1/3',
            }}
            options={{ slidesToScroll: 1, align: 'start', loop: true, dragFree: false }}
            slides={products.data.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                className={{ main: cn('', 'ea-product-labels-card') }}
              />
            ))}
          />
        </div>
        <div className="flex items-center justify-end gap-2 md:hidden">
          {showMoreLink && (
            <Button
              variant="accent"
              asChild
              className={cn(
                'w-full bg-input text-[#1E3C78] hover:bg-input',
                'ea-product-labels-container__button',
              )}
            >
              <Link href={`/${displayTag.slug}`} prefetch={false}>
                {props.title ?? t('text338')}
              </Link>
            </Button>
          )}
        </div>
        {props.children}
      </Container>
    </section>
  );
}

export function ProductLabelsContainer(props: ProductLabelsProps) {
  const loader = (
    <section
      id={props.id}
      className={cn(props.className, 'ea-product-labels-container')}
      style={props.style}
    >
      <Container
        size="xl"
        className={cn('flex flex-col space-y-6', 'ea-product-labels-container__container')}
      >
        <h2
          className={cn(
            'w-full text-left text-2xl font-bold',
            'ea-product-labels-container__title',
          )}
        >
          {props.title}
        </h2>
        <div
          className={cn(
            'flex flex-row items-stretch gap-4',
            'ea-product-labels-container__content',
          )}
        >
          {props.before}
          <div className={cn('flex w-full flex-row items-stretch gap-4 overflow-x-hidden')}>
            <Skeleton
              className={cn(
                'h-92 w-64 min-w-[16rem] shadow-lg',
                'ea-product-labels-container__skeleton',
              )}
            />
          </div>
          {props.after}
        </div>
      </Container>
    </section>
  );

  return (
    <Suspense fallback={loader}>
      <ProductLabelsInternal {...props} />
    </Suspense>
  );
}
